import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Paper,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  Settings as SettingsIcon,
  DescriptionOutlined as DescriptionIcon,
  FolderOutlined as FolderIcon,
  CalendarTodayOutlined as CalendarIcon,
  CheckCircleOutline as CheckCircleIcon,
  HourglassEmptyOutlined as HourglassIcon,
  ErrorOutline as ErrorIcon,
  CloudUploadOutlined as UploadIcon,
  Business as BusinessIcon,
  AutoAwesome as AIIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { Document } from '../types';
import { getDocumentById, dealsApi, getDeals } from '../services/api';
import DocumentMultimodalViewer from './DocumentMultimodalViewer';

interface DocumentDetailsModalProps {
  document: Document | null;
  open: boolean;
  onClose: () => void;
  loading?: boolean;
}

const DocumentDetailsModal: React.FC<DocumentDetailsModalProps> = ({
  document,
  open,
  onClose,
  loading = false,
}) => {
  const [detailedDocument, setDetailedDocument] = useState<any>(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Fetch detailed document information when modal opens
  useEffect(() => {
    if (open && document?.id && !loading) {
      setFetchingDetails(true);
      
      const fetchDocumentDetails = async () => {
        try {
          let response;
          
          // Check if this is a deal document
          if ((document as any).source === 'deal' || (document as any).source_type === 'deal') {
            console.log('Fetching deal document details for deal document:', document);
            
            // For deal documents, we need to find the deal ID first
            const dealName = (document as any).deal_name;
            if (dealName) {
              try {
                console.log(`Finding deal ID for deal: ${dealName}`);
                // Get all deals to find the matching deal ID
                const dealsResponse = await getDeals({ page: 1, size: 100 });
                const deals = dealsResponse?.deals || [];
                const matchingDeal = deals.find((deal: any) => deal.name === dealName);
                
                if (matchingDeal) {
                  console.log(`Found deal ID: ${matchingDeal.id} for deal: ${dealName}`);
                  // Use the deal-specific document API
                  response = await dealsApi.getDealDocument(matchingDeal.id, document.id);
                  console.log('Deal document details from deal API:', response);
                } else {
                  console.log(`Deal not found for name: ${dealName}, using basic info`);
                  response = document;
                }
              } catch (dealError) {
                console.log('Deal API failed, using basic info:', dealError);
                response = document;
              }
            } else {
              console.log('No deal name available, using basic info');
              response = document;
            }
          } else {
            // For project documents, use the general document API
            console.log('Fetching project document details:', document);
            response = await getDocumentById(document.id);
            console.log('Project document details:', response);
          }
          
          setDetailedDocument(response);
        } catch (err) {
          console.log('Document details not available, using basic info:', err);
          // Don't show error to user, just use basic document info
          setDetailedDocument(document);
        } finally {
          setFetchingDetails(false);
        }
      };
      
      fetchDocumentDetails();
    }
  }, [open, document?.id, loading, document]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setDetailedDocument(null);
      setFetchingDetails(false);
      setTabValue(0);
    }
  }, [open]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  if (!open) {
    return null;
  }

  const getStatusChip = (status: 'completed' | 'processing' | 'failed' | 'uploaded' | 'pending') => {
    switch (status) {
      case 'completed':
        return <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" variant="outlined" />;
      case 'processing':
        return <Chip icon={<HourglassIcon />} label="Processing" color="info" size="small" variant="outlined" />;
      case 'failed':
        return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" variant="outlined" />;
      case 'uploaded':
        return <Chip icon={<UploadIcon />} label="Uploaded" color="warning" size="small" variant="outlined" />;
      case 'pending':
        return <Chip icon={<HourglassIcon />} label="Pending" color="info" size="small" variant="outlined" />;
      default:
        return <Chip label="Unknown" size="small" variant="outlined" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <Paper sx={{
        borderRadius: '2rem',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-xl)',
        border: 'none',
      }}>
        <Box sx={{
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#6366f1',
          color: 'white',
        }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            {loading || !document ? (
              <Box sx={{
                height: 24,
                bgcolor: 'rgba(255,255,255,0.3)',
                borderRadius: 1,
                animation: 'pulse 1.5s ease-in-out infinite',
                width: '200px'
              }} />
            ) : (
              document.filename || document.name
            )}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Tabs for Details and Multimodal Content */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="document tabs">
            <Tab label="Details" icon={<InfoIcon />} iconPosition="start" />
            <Tab label="Images & Thumbnails" icon={<ImageIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ padding: '2rem', maxHeight: 'calc(90vh - 130px)', overflowY: 'auto' }}>
          {tabValue === 0 && (
            <>
              {(loading || fetchingDetails) && !detailedDocument ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading document details...</Typography>
            </Box>
          ) : (
            <>
              {/* Use detailedDocument if available, otherwise fallback to document */}
              {(() => {
                // Ensure filename is properly handled in the detailed document
                const displayDoc = detailedDocument || document;
                if (displayDoc && !displayDoc.name && displayDoc.filename) {
                  displayDoc.name = displayDoc.filename;
                }
                if (!displayDoc) return null;

                // Determine source information
                const getSourceInfo = () => {
                  if (displayDoc.source === 'deal' || displayDoc.deal_name) {
                    return {
                      icon: <BusinessIcon sx={{ mr: 0.5, color: 'var(--text-secondary)' }} />,
                      label: 'Deal:',
                      name: displayDoc.deal_name || 'Unknown Deal'
                    };
                  } else if (displayDoc.source === 'project' || displayDoc.project_name) {
                    return {
                      icon: <FolderIcon sx={{ mr: 0.5, color: 'var(--text-secondary)' }} />,
                      label: 'Project:',
                      name: displayDoc.project_name || 'Unknown Project'
                    };
                  } else {
                    return {
                      icon: <DescriptionIcon sx={{ mr: 0.5, color: 'var(--text-secondary)' }} />,
                      label: 'Source:',
                      name: 'General Documents'
                    };
                  }
                };

                const sourceInfo = getSourceInfo();

                return (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '2rem' }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InfoIcon sx={{ color: 'var(--primary-color)' }} />
                          Document Information
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Typography variant="body1">
                            <strong>Type:</strong>
                            <Chip 
                              label={displayDoc.type?.toUpperCase() || displayDoc.file_type?.toUpperCase() || 'N/A'} 
                              size="small" 
                              sx={{ ml: 1, background: 'var(--surface-color)', border: '1px solid var(--border-color)' }} 
                            />
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <strong>Status:</strong>
                            <Box ml={1}>{getStatusChip(displayDoc.processing_status as any)}</Box>
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            {sourceInfo.icon}
                            <strong>{sourceInfo.label}</strong>
                            <Typography component="span" sx={{ ml: 1, color: 'var(--text-secondary)' }}>
                              {sourceInfo.name}
                            </Typography>
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <UploadIcon sx={{ mr: 0.5, color: 'var(--text-secondary)' }} />
                            <strong>Uploaded By:</strong>
                            <Typography component="span" sx={{ ml: 1, color: 'var(--text-secondary)' }}>
                              {displayDoc.uploader_name || displayDoc.uploaded_by || 'N/A'}
                            </Typography>
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarIcon sx={{ mr: 0.5, color: 'var(--text-secondary)' }} />
                            <strong>Uploaded At:</strong>
                            <Typography component="span" sx={{ ml: 1, color: 'var(--text-secondary)' }}>
                              {new Date(displayDoc.uploaded_at as any).toLocaleDateString()}
                            </Typography>
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SettingsIcon sx={{ color: 'var(--primary-color)' }} />
                          File Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Typography variant="body1">
                            <strong>File Size:</strong> {formatFileSize(displayDoc.file_size || displayDoc.size || 0)}
                          </Typography>
                          <Typography variant="body1">
                            <strong>MIME Type:</strong> {displayDoc.mime_type || 'application/pdf'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Enhanced sections for detailed document */}
                    {detailedDocument && (
                      <>
                        {/* AI Summary Section */}
                        {detailedDocument.summary && (
                          <Box mt={4}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'var(--text-primary)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AIIcon sx={{ color: 'var(--primary-color)' }} />
                              AI Summary
                            </Typography>
                            <Box sx={{
                              p: 2,
                              borderRadius: 2,
                              background: 'rgba(102, 126, 234, 0.05)',
                              borderLeft: '4px solid var(--primary-color)',
                              color: 'var(--text-secondary)',
                              lineHeight: 1.6,
                              whiteSpace: 'pre-wrap',
                            }}>
                              {detailedDocument.summary}
                            </Box>
                          </Box>
                        )}

                        {/* Key Information Section */}
                        {detailedDocument.key_information && (
                          <Box mt={4}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'var(--text-primary)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InfoIcon sx={{ color: 'var(--primary-color)' }} />
                              Key Information
                            </Typography>
                            <Box sx={{
                              p: 2,
                              borderRadius: 2,
                              background: 'rgba(34, 197, 94, 0.05)',
                              borderLeft: '4px solid #22c55e',
                              color: 'var(--text-secondary)',
                              lineHeight: 1.6,
                              whiteSpace: 'pre-wrap',
                            }}>
                              {detailedDocument.key_information}
                            </Box>
                          </Box>
                        )}

                        {/* Analysis Section */}
                        {detailedDocument.analysis && (
                          <Box mt={4}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'var(--text-primary)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AIIcon sx={{ color: 'var(--primary-color)' }} />
                              AI Analysis
                            </Typography>
                            <Box sx={{
                              p: 2,
                              borderRadius: 2,
                              background: 'rgba(168, 85, 247, 0.05)',
                              borderLeft: '4px solid #a855f7',
                              color: 'var(--text-secondary)',
                              lineHeight: 1.6,
                              whiteSpace: 'pre-wrap',
                            }}>
                              {detailedDocument.analysis}
                            </Box>
                          </Box>
                        )}

                        {/* Tags Section */}
                        {detailedDocument.tags && detailedDocument.tags.length > 0 && (
                          <Box mt={4}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'var(--text-primary)', mb: 2 }}>
                              Tags
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {detailedDocument.tags.map((tag: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={tag}
                                  size="small"
                                  color={tag === 'deal' ? 'primary' : tag === 'project' ? 'secondary' : 'default'}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}

                    {/* Show message if no detailed info available */}
                    {!fetchingDetails && !detailedDocument?.summary && !detailedDocument?.key_information && !detailedDocument?.analysis && (
                      <Box mt={4} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No additional analysis available for this document yet.
                        </Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
            </>
          )}
          </>
          )}

          {/* Multimodal Content Tab */}
          {tabValue === 1 && document && (
            <DocumentMultimodalViewer
              documentId={document.id}
              documentName={document.filename || document.name}
            />
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default DocumentDetailsModal;
